import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { Card } from '@/components/Card';
import { useApi } from '@/hooks/useApi';
import { post } from '@/lib/api';

interface Job {
  id: string;
  title: string;
  status: string;
}

export function CandidateCreatePage() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { data: jobsData } = useApi<{ jobs: Job[] }>('jobs?status=open');

  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    job_id: '',
    source: 'direct',
    resume_url: '',
    linkedin_url: '',
    portfolio_url: '',
    tags: '',
    notes: '',
  });

  function update(field: string, value: string) {
    setForm(f => ({ ...f, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim() || !form.job_id) {
      setError('First name, last name, email, and job are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const selectedJob = (jobsData?.jobs ?? []).find(j => j.id === form.job_id);
      const payload: Record<string, unknown> = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        job_id: form.job_id,
        job_title: selectedJob?.title ?? '',
        phone: form.phone || undefined,
        source: form.source,
        resume_url: form.resume_url || undefined,
        linkedin_url: form.linkedin_url || undefined,
        portfolio_url: form.portfolio_url || undefined,
        tags: form.tags ? form.tags.split(',').map(s => s.trim()).filter(Boolean) : [],
        notes: form.notes || undefined,
      };
      const result = await post<{ id: string }>('candidates', payload);
      navigate(`/candidates/${result.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create candidate');
    } finally {
      setSaving(false);
    }
  }

  const inputClass = 'w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-amber-500';
  const labelClass = 'text-xs font-medium text-muted-foreground block mb-1';

  return (
    <div className="space-y-6 max-w-3xl">
      <button onClick={() => navigate('/candidates')} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="w-4 h-4" /> Back to Candidates
      </button>

      <h2 className="text-xl font-bold text-foreground">Add New Candidate</h2>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg text-sm">{error}</div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <h3 className="text-sm font-semibold text-foreground mb-4">Candidate Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>First Name *</label>
              <input value={form.first_name} onChange={e => update('first_name', e.target.value)} placeholder="Jane" className={inputClass} required />
            </div>
            <div>
              <label className={labelClass}>Last Name *</label>
              <input value={form.last_name} onChange={e => update('last_name', e.target.value)} placeholder="Smith" className={inputClass} required />
            </div>
            <div>
              <label className={labelClass}>Email *</label>
              <input type="email" value={form.email} onChange={e => update('email', e.target.value)} placeholder="jane@example.com" className={inputClass} required />
            </div>
            <div>
              <label className={labelClass}>Phone</label>
              <input type="tel" value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="+1 555-0123" className={inputClass} />
            </div>
          </div>
        </Card>

        <Card>
          <h3 className="text-sm font-semibold text-foreground mb-4">Application Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Job Position *</label>
              <select value={form.job_id} onChange={e => update('job_id', e.target.value)} className={inputClass} required>
                <option value="">Select a job...</option>
                {(jobsData?.jobs ?? []).map(job => (
                  <option key={job.id} value={job.id}>{job.title}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Source</label>
              <select value={form.source} onChange={e => update('source', e.target.value)} className={inputClass}>
                <option value="direct">Direct Application</option>
                <option value="referral">Employee Referral</option>
                <option value="linkedin">LinkedIn</option>
                <option value="indeed">Indeed</option>
                <option value="recruiter">Recruiter</option>
                <option value="career_site">Career Site</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Resume URL</label>
              <input value={form.resume_url} onChange={e => update('resume_url', e.target.value)} placeholder="https://..." className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>LinkedIn URL</label>
              <input value={form.linkedin_url} onChange={e => update('linkedin_url', e.target.value)} placeholder="https://linkedin.com/in/..." className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Portfolio URL</label>
              <input value={form.portfolio_url} onChange={e => update('portfolio_url', e.target.value)} placeholder="https://..." className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Tags (comma-separated)</label>
              <input value={form.tags} onChange={e => update('tags', e.target.value)} placeholder="senior, remote, security" className={inputClass} />
            </div>
          </div>
        </Card>

        <Card>
          <h3 className="text-sm font-semibold text-foreground mb-4">Notes</h3>
          <textarea value={form.notes} onChange={e => update('notes', e.target.value)} placeholder="Any notes about this candidate..." rows={3} className={inputClass} />
        </Card>

        <div className="flex items-center justify-end gap-3">
          <button type="button" onClick={() => navigate('/candidates')} className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground rounded-lg hover:bg-surface-2">
            Cancel
          </button>
          <button type="submit" disabled={saving} className="flex items-center gap-2 px-6 py-2 bg-amber-500 text-foreground rounded-lg text-sm font-medium hover:bg-amber-600 disabled:opacity-50 transition-colors">
            <Save className="w-4 h-4" /> {saving ? 'Creating...' : 'Add Candidate'}
          </button>
        </div>
      </form>
    </div>
  );
}
